import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  ResourceList,
  TextStyle,
  Stack,
  Heading,
  Badge,
  Button,
  ButtonGroup,
  Spinner,
  EmptyState,
  Text,
  TextContainer,
  ProgressBar,
  Tabs,
  Banner,
  DataTable,
  Icon,
  Collapsible,
  List,
  Tooltip,
  Link,
  Modal,
  Divider
} from '@shopify/polaris';

import {
  StoreMajor,
  ProductsMajor,
  TemplateMajor,
  EditMajor,
  CreditCardMajor,
  AutomationMajor,
  CalendarMajor,
  CirclePlusOutlineMinor,
  StarOutlineMinor,
  ChevronDownMinor,
  ChevronUpMinor,
  QuestionMarkMajor,
  PlayMajor,
  InfoMinor,
  TickMinor
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../contexts/ShopContext';
import { useTemplate } from '../contexts/TemplateContext';
import { useAuth } from '../contexts/AuthContext';
import * as templateUsageService from '../services/templateUsageService';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { checkAndProcessRenewal } from '../services/creditService';
import { getCreditStats, getUserCreditStatus } from '../services/optimizedCreditService';
import { getImageProcessingMetrics } from '../services/imageMetricsService';
import { getUserSubscription, fixSubscriptionResetDates } from '../services/subscriptionService';
import { getAllShopsMetrics } from '../services/optimizedShopMetricsService';
import { getCreditUsageStats } from '../services/optimizedCreditService';
import UserCreditsInfo from '../components/UserCreditsInfo';
import MaintenanceBanner from '../components/MaintenanceBanner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Legend,
  ComposedChart,
  Area,
  Brush,
  Bar
} from 'recharts';

// Define consistent chart colors
const CHART_COLORS = {
  primary: '#007A5C',    // Base green color
  secondary: '#5C6AC4',  // Complementary blue
  tertiary: '#9C6ADE',   // Purple for third data series
  quaternary: '#F49342', // Orange for fourth data series
  success: '#008060',    // Success green
  warning: '#FFC453',    // Warning yellow
  error: '#DE3618',      // Error red
  brushStroke: '#8884d8' // Brush component color
};

function Dashboard() {
  const navigate = useNavigate();
  const { shops, currentShop, setShops } = useShop();
  const { templates, workflows, plans } = useTemplate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templateUsage, setTemplateUsage] = useState({
    product: 0,
    variant: 0,
    templates: {}
  });
  const [creditStats, setCreditStats] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [creditWarning, setCreditWarning] = useState(null);
  const [imageMetrics, setImageMetrics] = useState(null);
  const [pushMetrics, setPushMetrics] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [allShopsMetrics, setAllShopsMetrics] = useState(null);
  const [creditUsageStats, setCreditUsageStats] = useState(null);
  
  // Getting Started section state
  const [gettingStartedOpen, setGettingStartedOpen] = useState(false);
  const [tutorialModalOpen, setTutorialModalOpen] = useState(false);
  const [templateInfoModalOpen, setTemplateInfoModalOpen] = useState(false);
  const [aiModelInfoModalOpen, setAiModelInfoModalOpen] = useState(false);
  const [onboardingState, setOnboardingState] = useState({
    productsViewed: false,
    aiModelsExplored: false,
    tutorialsWatched: false
  });

  // Fetch onboarding state from Firebase
  useEffect(() => {
    const fetchOnboardingState = async () => {
      if (!user?.uid) return;
      
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists() && userDoc.data().onboarding) {
          setOnboardingState(prevState => ({
            ...prevState,
            ...userDoc.data().onboarding
          }));
        }
      } catch (error) {
        console.error('Error fetching onboarding state:', error);
      }
    };
    
    fetchOnboardingState();
  }, [user]);
  
  // Function to update onboarding state in Firebase
  const updateOnboardingState = async (key, value) => {
    if (!user?.uid) return;
    
    try {
      // Update local state
      setOnboardingState(prevState => ({
        ...prevState,
        [key]: value
      }));
      
      // Update in Firebase
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Update existing document
        await updateDoc(userRef, {
          [`onboarding.${key}`]: value
        });
      } else {
        // Create new document with onboarding data
        await setDoc(userRef, {
          onboarding: { [key]: value }
        });
      }
    } catch (error) {
      console.error('Error updating onboarding state:', error);
    }
  };
  
  // Fetch template usage from Firebase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        if (!user?.uid) {
          console.log('No user ID available');
          return;
        }

        console.log('Loading data for user:', user.uid);

        // Load template usage directly from user document
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          console.log('User document not found');
          return;
        }

        const userData = userDoc.data();
        console.log('User data loaded:', userData);
        
        if (userData?.templateUsage) {
          const usage = {
            product: userData.templateUsage.product || 0,
            variant: userData.templateUsage.variant || 0,
            templates: userData.templateUsage.templates || {}
          };
          console.log('Setting template usage:', usage);
          setTemplateUsage(usage);
        }

        // Load image processing metrics
        const metrics = await getImageProcessingMetrics(user.uid);
        setImageMetrics(metrics);
        
        // Get push metrics from credit stats (will be loaded in the next useEffect)
        
        // Load metrics for all shops
        const shopsMetrics = await getAllShopsMetrics(user.uid);
        setAllShopsMetrics(shopsMetrics);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Load credit stats and subscription info
  useEffect(() => {
    const loadCreditInfo = async () => {
      if (!user?.uid) return;
      try {
        // Fix subscription reset dates for existing subscriptions
        await fixSubscriptionResetDates(user.uid);
        
        // Check for subscription renewal
        await checkAndProcessRenewal(user.uid);
        
        // Get credit status including subscription info
        const status = await getUserCreditStatus(user.uid);
        
        // Debug log to see what's in the subscription data
        console.log('Credit status from Firebase:', JSON.stringify(status, null, 2));
        console.log('Subscription data:', status.subscription);
        console.log('VIP subscription data:', status.vipSubscription);
        console.log('Reset dates:', {
          nextCreditResetDate: status.subscription?.nextCreditResetDate,
          nextBillingDate: status.subscription?.nextBillingDate,
          currentPeriodEnd: status.subscription?.currentPeriodEnd,
          periodEndTimestamp: status.subscription?.periodEndTimestamp
        });
        
        setCreditStatus(status);
        setUserSubscription(status.subscription);
        
        // Get detailed credit stats
        const stats = await getCreditStats(user.uid);
        console.log('Credit stats loaded:', stats);
        setCreditStats(stats);
        
        // Get credit usage statistics
        const usageStats = await getCreditUsageStats(user.uid, 6); // Get 6 months of data
        setCreditUsageStats(usageStats);
        console.log('Credit usage stats loaded:', usageStats);
        console.log('Size chart credits:', usageStats.usageByType.sizechart);
        console.log('Credit usage by type:', usageStats.usageByType);
        console.log('Raw credit usage stats document:', usageStats);
        console.log('byShop data in credit stats:', usageStats.byShop);
        console.log('Monthly trend data:', usageStats.monthlyTrend);
        
        // Extract push metrics from credit usage stats
        if (usageStats && usageStats.monthlyTrend) {
          const pushData = {
            monthly: usageStats.monthlyTrend.map(month => ({
              month: month.month,
              monthName: month.monthName,
              pushes: month.pushes || 0
            }))
          };
          setPushMetrics(pushData);
        }
      } catch (error) {
        console.error('Error loading credit info:', error);
      }
    };
    loadCreditInfo();
  }, [user]);

  // Check credit status and set warning
  useEffect(() => {
    if (creditStatus && creditStatus.credits) {
      const { monthly = 0, flex = 0 } = creditStatus.credits;
      const available = monthly + flex;
      
      if (available <= 10) {
        setCreditWarning({
          status: 'critical',
          message: `You have only ${available} credits left! Please consider purchasing additional flex credits.`
        });
      } else if (monthly <= 10 && creditStatus.subscription) {
        setCreditWarning({
          status: 'warning',
          message: `You have only ${monthly} monthly credits left. They will reset on your next billing date.`
        });
      } else {
        setCreditWarning(null);
      }
    }
  }, [creditStatus]);

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Handle Firestore timestamp objects
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString();
      }
      
      // Handle raw Unix timestamps (seconds since epoch)
      if (typeof timestamp === 'number') {
        return new Date(timestamp * 1000).toLocaleDateString();
      }
      
      // Handle Date objects
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString();
      }
      
      // Handle string timestamps
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      
      return 'N/A';
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };

  // Count templates by type
  const templateStats = templates.reduce((acc, template) => {
    acc[template.type] = (acc[template.type] || 0) + 1;
    return acc;
  }, {});

  // Credit and subscription card
  const renderCreditCard = () => {
    const { credits = {}, subscription = null, vipSubscription = null, isVIP = false, vipPlan = null } = creditStatus || {};
    const { monthly = 0, flex = 0, used = 0 } = credits;
    const totalAvailable = monthly + flex;
    
    // Determine which subscription to display
    const activeSubscription = isVIP && vipSubscription ? vipSubscription : subscription;
    
    // Format VIP end date display
    let vipEndDateDisplay = null;
    if (isVIP && vipSubscription) {
      if (vipSubscription.endDate) {
        vipEndDateDisplay = formatDate(vipSubscription.endDate);
      } else {
        vipEndDateDisplay = 'Unlimited';
      }
    }
    
    return (
      <Card>
        <Card.Section>
          <Stack vertical spacing="tight">
            <Stack distribution="equalSpacing" alignment="center">
              <Stack vertical spacing="extraTight">
                <TextStyle variation="strong">Subscription Plan</TextStyle>
                <div>
                  {isVIP && vipSubscription ? (
                    <Badge status="attention">
                      VIP: {vipSubscription.planName || 
                           (plans && vipPlan && plans[vipPlan] ? plans[vipPlan].name : 'Unknown Plan')}
                    </Badge>
                  ) : subscription ? (
                    <Badge status="success">
                      {console.log('Dashboard - Subscription Type:', subscription.type)}
                      {console.log('Dashboard - Plans available:', plans ? Object.keys(plans) : 'No plans available')}
                      {console.log('Dashboard - Plan from plans object:', plans && subscription.type ? plans[subscription.type]?.name : 'No plan found')}
                      {console.log('Dashboard - Plan name from subscription:', subscription.planName)}
                      {subscription.planName || 
                       (plans && subscription.type && plans[subscription.type] ? plans[subscription.type].name : 'Unknown Plan')}
                    </Badge>
                  ) : (
                    <Badge status="attention">No active plan</Badge>
                  )}
                </div>
              </Stack>
              
              {activeSubscription && (
                <Stack vertical spacing="extraTight" alignment="trailing">
                  <TextStyle variation="strong">Current Period End</TextStyle>
                  <Stack alignment="center" spacing="extraTight">
                    <Icon source={CalendarMajor} color="base" />
                    {isVIP && vipSubscription ? (
                      <TextStyle>{vipEndDateDisplay}</TextStyle>
                    ) : (
                      <TextStyle>{formatDate(subscription?.periodEndTimestamp || subscription?.currentPeriodEnd)}</TextStyle>
                    )}
                  </Stack>
                </Stack>
              )}
            </Stack>
            
            <div style={{ marginTop: '1rem' }}>
              <Stack distribution="equalSpacing" alignment="center">
                <TextStyle variation="strong">Monthly Credits: {monthly}/{activeSubscription?.monthlyCredits || 0}</TextStyle>
                <TextStyle variation="subdued">Total Credits Used: {used}</TextStyle>
              </Stack>
              <div style={{ marginTop: '0.5rem' }}>
                <ProgressBar 
                  progress={activeSubscription?.monthlyCredits > 0 ? Math.min(100, Math.round((monthly / activeSubscription.monthlyCredits) * 100)) : 0} 
                  size="small" 
                />
              </div>
            </div>
            
            <div style={{ marginTop: '1rem' }}>
              <Stack distribution="equalSpacing">
                <Stack vertical spacing="extraTight">
                  <Stack alignment="center" spacing="extraTight">
                    <Icon source={StarOutlineMinor} color="base" />
                    <TextStyle variation="strong">Monthly Credits</TextStyle>
                  </Stack>
                  <Badge status={monthly > 0 ? "success" : "attention"}>
                    {monthly}
                  </Badge>
                  {activeSubscription && (
                    <TextStyle variation="subdued" size="small">
                      Resets on {formatDate(activeSubscription.nextCreditResetDate || activeSubscription.nextBillingDate || activeSubscription.currentPeriodEnd)}
                    </TextStyle>
                  )}
                </Stack>
                
                <Stack vertical spacing="extraTight">
                  <Stack alignment="center" spacing="extraTight">
                    <Icon source={CirclePlusOutlineMinor} color="base" />
                    <TextStyle variation="strong">Flex Credits</TextStyle>
                  </Stack>
                  <Badge status={flex > 0 ? "success" : "attention"}>
                    {flex}
                  </Badge>
                  <TextStyle variation="subdued" size="small">
                    Never expire
                  </TextStyle>
                </Stack>
              </Stack>
            </div>
            
            {/* Credit Usage Breakdown */}
            {creditUsageStats && (
              <div style={{ marginTop: '1rem' }}>
                <Divider />
                <div style={{ marginTop: '1rem' }}>
                  <TextStyle variation="strong">Credit Usage Breakdown</TextStyle>
                  <div style={{ marginTop: '0.5rem' }}>
                    <Stack distribution="equalSpacing">
                      <Stack vertical spacing="extraTight">
                        <TextStyle variation="subdued" size="small">Image Credits</TextStyle>
                        <Badge>{creditUsageStats.usageByType.image || 0}</Badge>
                      </Stack>
                      <Stack vertical spacing="extraTight">
                        <TextStyle variation="subdued" size="small">Template Credits</TextStyle>
                        <Badge>{creditUsageStats.usageByType.template || 0}</Badge>
                      </Stack>
                      <Stack vertical spacing="extraTight">
                        <TextStyle variation="subdued" size="small">Sizechart Credits</TextStyle>
                        <Badge>{creditUsageStats.usageByType.sizechart || 0}</Badge>
                      </Stack>
                    </Stack>
                  </div>
                </div>
              </div>
            )}
            
            {creditWarning && (
              <div style={{ marginTop: '1rem' }}>
                <Banner status={creditWarning.status}>
                  <p>{creditWarning.message}</p>
                </Banner>
              </div>
            )}
            
            <div style={{ marginTop: '1rem' }}>
              <Stack distribution="equalSpacing">
                <Button onClick={() => navigate('/app/subscription')} primary>
                  Upgrade Plan
                </Button>
                <Button onClick={() => navigate('/app/credits')}>
                  Purchase Credits
                </Button>
              </Stack>
            </div>
          </Stack>
        </Card.Section>
      </Card>
    );
  };

  const statsCards = [
    {
      title: 'Connected Shops',
      value: shops.length,
      icon: StoreMajor,
      action: () => navigate('/app/shops'),
    },
    {
      title: 'Total Templates',
      value: templates.length,
      icon: TemplateMajor,
    },
    {
      title: 'Total Flows',
      value: workflows.length,
      icon: AutomationMajor,
    }
  ];

  // Get the most recently connected shops
  const recentShops = [...shops]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5)
    .map(shop => ({
      ...shop,
      // A shop is active if it's the current shop
      active: currentShop?.id === shop.id
    }));
    
  // Define checklist items for Getting Started section
  const checklistItems = [
    { text: 'Connect your Shopify store', completed: shops.length > 0, action: () => navigate('/app/connect') },
    { text: 'Select products to edit', completed: creditStats?.totalCreditsUsed > 0 || onboardingState.productsViewed, 
      action: () => {
        updateOnboardingState('productsViewed', true);
        navigate('/app/products');
      }
    },
    { text: 'Create and use templates', completed: templates.length > 0, action: () => setTemplateInfoModalOpen(true) },
    { text: 'Explore AI model options', completed: onboardingState.aiModelsExplored, 
      action: () => {
        updateOnboardingState('aiModelsExplored', true);
        setAiModelInfoModalOpen(true);
      }
    },
    { text: 'Watch tutorial videos', completed: onboardingState.tutorialsWatched, 
      action: () => {
        updateOnboardingState('tutorialsWatched', true);
        setTutorialModalOpen(true);
      }
    }
  ];
  
  // Section descriptions for tooltips
  const sectionDescriptions = {
    accountSubscription: 'View your subscription details, available credits, and account statistics. Upgrade your plan or purchase additional credits here.',
    usageMetrics: 'Track your credit usage over time, see how many images you\'ve processed, and monitor your storage savings.',
    templatePerformance: 'See which templates you use most frequently and how many products you\'ve processed with each template type.',
    shopManagement: 'Manage your connected Shopify shops, view shop-specific metrics, and quickly access your products.'
  };

  // Tutorial videos content
  const tutorialVideos = [
    { title: 'Getting Started Guide', url: '#', description: 'Learn the basics of Editify' },
    { title: 'Creating Templates', url: '#', description: 'How to create and use AI templates effectively' },
    { title: 'Bulk Editing Products', url: '#', description: 'Process multiple products at once' },
    { title: 'Bulk Image Editing', url: '#', description: 'Optimize and process product images in bulk' },
    { title: 'Advanced Features', url: '#', description: 'Get the most out of your subscription' }
  ];

  // Template creation workflow content
  const templateWorkflowSteps = [
    { title: '1. Select Products', description: 'Go to the Products page and select the products you want to edit.' },
    { title: '2. Access Template Manager', description: 'Click on the "Manage Templates" button in the product editing interface.' },
    { title: '3. Create New Template', description: 'Click the "Create New Template" button to start building your template.' },
    { title: '4. Configure Template', description: 'Select input fields, output fields, and customize your template settings.' },
    { title: '5. Save & Use', description: 'Save your template and apply it to products for AI-powered content generation.' }
  ];

  // AI model selection workflow content
  const aiModelWorkflowSteps = [
    { title: '1. Start Template Creation', description: 'Begin by creating or editing a template from the product editing interface.' },
    { title: '2. Open AI Model Settings', description: 'In the template editor, look for the AI model selection dropdown.' },
    { title: '3. Select Model', description: 'Choose from available AI models based on your needs (general text, image analysis, etc.).' },
    { title: '4. Review Credit Costs', description: 'Different models have different credit costs - review before finalizing.' },
    { title: '5. Save Preferences', description: 'Your AI model selection will be saved with the template.' }
  ];

  if (!currentShop && shops.length === 0) {
    return (
      <Page title="Dashboard">
        <MaintenanceBanner />
        
        {/* Tutorial Videos Modal */}
        <Modal
          open={tutorialModalOpen}
          onClose={() => setTutorialModalOpen(false)}
          title="Tutorial Videos"
          primaryAction={{
            content: 'Close',
            onAction: () => setTutorialModalOpen(false),
          }}
        >
          <Modal.Section>
            <Stack vertical>
              {tutorialVideos.map((video, index) => (
                <Card key={index}>
                  <Card.Section>
                    <Stack alignment="center" spacing="tight">
                      <div style={{ marginRight: '12px' }}>
                        <Icon source={PlayMajor} color="base" />
                      </div>
                      <Stack vertical spacing="extraTight">
                        <Link url={video.url}>
                          <Text variant="headingSm" as="h3">{video.title}</Text>
                        </Link>
                        <Text variant="bodySm" as="p">{video.description}</Text>
                      </Stack>
                    </Stack>
                  </Card.Section>
                </Card>
              ))}
            </Stack>
          </Modal.Section>
        </Modal>
        
        {/* Template Creation Workflow Modal */}
        <Modal
          open={templateInfoModalOpen}
          onClose={() => setTemplateInfoModalOpen(false)}
          title="How to Create Templates"
          primaryAction={{
            content: 'Go to Products',
            onAction: () => {
              setTemplateInfoModalOpen(false);
              navigate('/app/products');
            },
          }}
          secondaryActions={[
            {
              content: 'Close',
              onAction: () => setTemplateInfoModalOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <TextContainer>
              <Text variant="bodyMd" as="p">
                Templates allow you to automate product content creation using AI. Follow these steps to create and use templates:
              </Text>
            </TextContainer>
            
            <div style={{ marginTop: '16px' }}>
              {templateWorkflowSteps.map((step, index) => (
                <Card key={index} sectioned>
                  <Stack vertical spacing="tight">
                    <Text variant="headingSm" as="h3">{step.title}</Text>
                    <Text variant="bodyMd" as="p">{step.description}</Text>
                  </Stack>
                </Card>
              ))}
            </div>
          </Modal.Section>
        </Modal>
        
        {/* AI Model Selection Workflow Modal */}
        <Modal
          open={aiModelInfoModalOpen}
          onClose={() => setAiModelInfoModalOpen(false)}
          title="How to Select AI Models"
          primaryAction={{
            content: 'Go to Products',
            onAction: () => {
              setAiModelInfoModalOpen(false);
              navigate('/app/products');
            },
          }}
          secondaryActions={[
            {
              content: 'Close',
              onAction: () => setAiModelInfoModalOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <TextContainer>
              <Text variant="bodyMd" as="p">
                Different AI models offer various capabilities and cost different amounts of credits. Here's how to select the right model for your templates:
              </Text>
            </TextContainer>
            
            <div style={{ marginTop: '16px' }}>
              {aiModelWorkflowSteps.map((step, index) => (
                <Card key={index} sectioned>
                  <Stack vertical spacing="tight">
                    <Text variant="headingSm" as="h3">{step.title}</Text>
                    <Text variant="bodyMd" as="p">{step.description}</Text>
                  </Stack>
                </Card>
              ))}
            </div>
          </Modal.Section>
        </Modal>
        
        {/* Getting Started Expandable Section for new users */}
        <div style={{ marginBottom: '16px' }}>
          <Card>
            <Card.Section>
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => setGettingStartedOpen(!gettingStartedOpen)}
              >
                <Stack alignment="center" distribution="equalSpacing">
                  <Stack alignment="center" spacing="tight">
                    <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
                      <span role="img" aria-label="rocket" style={{ fontSize: '20px' }}>🚀</span>
                    </div>
                    <Text variant="headingMd" as="h2">Getting Started</Text>
                  </Stack>
                  <Stack alignment="center" spacing="tight">
                    <Button
                      plain
                      icon={tutorialModalOpen ? undefined : PlayMajor}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTutorialModalOpen(true);
                      }}
                    >
                      Tutorials
                    </Button>
                    <Button plain icon={gettingStartedOpen ? ChevronUpMinor : ChevronDownMinor} />
                  </Stack>
                </Stack>
              </div>
              
              <Collapsible
                open={gettingStartedOpen}
                id="getting-started-collapsible"
              >
                <div style={{ marginTop: '16px' }}>
                  <Text variant="bodyMd" as="p">Complete these steps to get the most out of Editify:</Text>
                  <div style={{ marginTop: '12px' }}>
                    {checklistItems.map((item, index) => (
                      <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '50%', 
                          backgroundColor: item.completed ? '#008060' : '#f4f6f8',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '12px'
                        }}>
                          {item.completed ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6.66675 10.1148L12.7947 3.98608L13.7381 4.92875L6.66675 12.0001L2.42675 7.76008L3.37008 6.81675L6.66675 10.1148Z" fill="white" />
                            </svg>
                          ) : (
                            <Text variant="bodyMd" as="span">{index + 1}</Text>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text variant="bodyMd" as="span">
                            {item.text}
                          </Text>
                        </div>
                        <div>
                          <Button size="slim" onClick={item.action}>
                            {item.completed ? 'View' : 'Start'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Collapsible>
            </Card.Section>
          </Card>
        </div>
        
        <Layout>
          <Layout.Section>
            {renderCreditCard()}
          </Layout.Section>
          <Layout.Section>
            <EmptyState
              heading="Connect your first Shopify store"
              action={{
                content: 'Connect Store',
                onAction: () => navigate('/app/shops/connect')
              }}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Connect your Shopify store to start creating and editing products.</p>
            </EmptyState>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Dashboard">
      <MaintenanceBanner />
      
      {/* Getting Started Expandable Section */}
      <div style={{ marginBottom: '16px' }}>
        <Card>
          <Card.Section>
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => setGettingStartedOpen(!gettingStartedOpen)}
            >
              <Stack alignment="center" distribution="equalSpacing">
                <Stack alignment="center" spacing="tight">
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
                    <span role="img" aria-label="rocket" style={{ fontSize: '20px' }}>🚀</span>
                  </div>
                  <Text variant="headingMd" as="h2">Getting Started</Text>
                </Stack>
                <Stack alignment="center" spacing="tight">
                  <Button
                    plain
                    icon={tutorialModalOpen ? undefined : PlayMajor}
                    onClick={(e) => {
                      e.stopPropagation();
                      setTutorialModalOpen(true);
                    }}
                  >
                    Tutorials
                  </Button>
                  <Button plain icon={gettingStartedOpen ? ChevronUpMinor : ChevronDownMinor} />
                </Stack>
              </Stack>
            </div>
            
            <Collapsible
              open={gettingStartedOpen}
              id="getting-started-collapsible"
            >
              <div style={{ marginTop: '16px' }}>
                <Text variant="bodyMd" as="p">Complete these steps to get the most out of Editify:</Text>
                <div style={{ marginTop: '12px' }}>
                  {checklistItems.map((item, index) => (
                    <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                      <div style={{ 
                        width: '24px', 
                        height: '24px', 
                        borderRadius: '50%', 
                        backgroundColor: item.completed ? '#008060' : '#f4f6f8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px'
                      }}>
                        {item.completed ? (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.66675 10.1148L12.7947 3.98608L13.7381 4.92875L6.66675 12.0001L2.42675 7.76008L3.37008 6.81675L6.66675 10.1148Z" fill="white" />
                          </svg>
                        ) : (
                          <Text variant="bodyMd" as="span">{index + 1}</Text>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Text variant="bodyMd" as="span">
                          {item.text}
                        </Text>
                      </div>
                      <div>
                        <Button size="slim" onClick={item.action}>
                          {item.completed ? 'View' : 'Start'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Collapsible>
          </Card.Section>
        </Card>
      </div>
      
      {/* Tutorial Videos Modal */}
      {/* Tutorial Videos Modal */}
      <Modal
        open={tutorialModalOpen}
        onClose={() => setTutorialModalOpen(false)}
        title="Tutorial Videos"
        primaryAction={{
          content: 'Close',
          onAction: () => setTutorialModalOpen(false),
        }}
      >
        <Modal.Section>
          <Stack vertical>
            {tutorialVideos.map((video, index) => (
              <Card key={index}>
                <Card.Section>
                  <Stack alignment="center" spacing="tight">
                    <div style={{ marginRight: '12px' }}>
                      <Icon source={PlayMajor} color="base" />
                    </div>
                    <Stack vertical spacing="extraTight">
                      <Link url={video.url}>
                        <Text variant="headingSm" as="h3">{video.title}</Text>
                      </Link>
                      <Text variant="bodySm" as="p">{video.description}</Text>
                    </Stack>
                  </Stack>
                </Card.Section>
              </Card>
            ))}
          </Stack>
        </Modal.Section>
      </Modal>
      
      {/* Template Creation Workflow Modal */}
      <Modal
        open={templateInfoModalOpen}
        onClose={() => setTemplateInfoModalOpen(false)}
        title="How to Create Templates"
        primaryAction={{
          content: 'Go to Products',
          onAction: () => {
            setTemplateInfoModalOpen(false);
            navigate('/app/products');
          },
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: () => setTemplateInfoModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <Text variant="bodyMd" as="p">
              Templates allow you to automate product content creation using AI. Follow these steps to create and use templates:
            </Text>
          </TextContainer>
          
          <div style={{ marginTop: '16px' }}>
            {templateWorkflowSteps.map((step, index) => (
              <Card key={index} sectioned>
                <Stack vertical spacing="tight">
                  <Text variant="headingSm" as="h3">{step.title}</Text>
                  <Text variant="bodyMd" as="p">{step.description}</Text>
                </Stack>
              </Card>
            ))}
          </div>
        </Modal.Section>
      </Modal>
      
      {/* AI Model Selection Workflow Modal */}
      <Modal
        open={aiModelInfoModalOpen}
        onClose={() => setAiModelInfoModalOpen(false)}
        title="How to Select AI Models"
        primaryAction={{
          content: 'Go to Products',
          onAction: () => {
            setAiModelInfoModalOpen(false);
            navigate('/app/products');
          },
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: () => setAiModelInfoModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <Text variant="bodyMd" as="p">
              Different AI models offer various capabilities and cost different amounts of credits. Here's how to select the right model for your templates:
            </Text>
          </TextContainer>
          
          <div style={{ marginTop: '16px' }}>
            {aiModelWorkflowSteps.map((step, index) => (
              <Card key={index} sectioned>
                <Stack vertical spacing="tight">
                  <Text variant="headingSm" as="h3">{step.title}</Text>
                  <Text variant="bodyMd" as="p">{step.description}</Text>
                </Stack>
              </Card>
            ))}
          </div>
        </Modal.Section>
      </Modal>
      <Layout>
        {/* SECTION 1: ACCOUNT & SUBSCRIPTION */}
        <Layout.Section>
          <div style={{ marginBottom: '16px' }}>
            <TextContainer spacing="tight">
              <Stack alignment="center" spacing="tight">
                <Text variant="headingMd" as="h2">Account & Subscription</Text>
                <Tooltip content={sectionDescriptions.accountSubscription}>
                  <Button plain icon={InfoMinor} accessibilityLabel="Learn more about Account & Subscription" />
                </Tooltip>
              </Stack>
              <Text variant="bodySm" as="p">Your subscription details, available credits, and account statistics.</Text>
            </TextContainer>
          </div>
          <Card>
            {/* Credit and Subscription Information */}
            <Card.Section>
              {loading ? (
                <Stack distribution="center">
                  <Spinner size="large" />
                </Stack>
              ) : (
                renderCreditCard()
              )}
            </Card.Section>
            
            {/* Account Statistics */}
            <Card.Section title="Account Statistics">
              <Stack distribution="fillEvenly">
                {statsCards.map((stat, index) => (
                  <Stack vertical alignment="center" key={index}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <Icon source={stat.icon} color="base" />
                    </div>
                    <Text variant="headingLg" as="h3">
                      {stat.value}
                    </Text>
                    <Text variant="bodySm" as="p">
                      {stat.title}
                    </Text>
                    {stat.action && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <Button size="slim" onClick={stat.action}>
                          View
                        </Button>
                      </div>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Card.Section>
          </Card>
        </Layout.Section>

        {/* SECTION 2: CREDITS & TEMPLATES */}
        <Layout.Section>
          <div style={{ marginBottom: '16px' }}>
            <TextContainer spacing="tight">
              <Stack alignment="center" spacing="tight">
                <Text variant="headingMd" as="h2">Usage Metrics</Text>
                <Tooltip content={sectionDescriptions.usageMetrics}>
                  <Button plain icon={InfoMinor} accessibilityLabel="Learn more about Usage Metrics" />
                </Tooltip>
                <Button
                  size="slim"
                  onClick={async () => {
                    try {
                      // Reload credit usage statistics
                      const usageStats = await getCreditUsageStats(user.uid, 6);
                      setCreditUsageStats(usageStats);
                      console.log('Refreshed credit usage stats:', usageStats);
                      console.log('Size chart credits:', usageStats.usageByType.sizechart);
                    } catch (error) {
                      console.error('Error refreshing credit usage stats:', error);
                    }
                  }}
                >
                  Refresh Stats
                </Button>
              </Stack>
              <Text variant="bodySm" as="p">Track your credit usage, image processing, and storage savings over time.</Text>
            </TextContainer>
          </div>
          <Card>
            {/* Credits Usage Stats */}
            <Card.Section title="Credits Usage Stats">
              <Stack distribution="fillEvenly" alignment="center">
                <Stack vertical alignment="center">
                  <Text variant="headingLg" as="h3">
                    {creditStats?.used || 0}
                  </Text>
                  <Text variant="bodySm" as="p">
                    Total Credits Used
                  </Text>
                </Stack>
                <Stack vertical alignment="center">
                  <Text variant="headingLg" as="h3">
                    {creditStats?.available || 0}
                  </Text>
                  <Text variant="bodySm" as="p">
                    Remaining Credits
                  </Text>
                </Stack>
                <Stack vertical alignment="center">
                  <Text variant="headingLg" as="h3">
                    {creditStats?.thisMonth || 0}
                  </Text>
                  <Text variant="bodySm" as="p">
                    Credits Used This Month
                  </Text>
                </Stack>
              </Stack>
            </Card.Section>
            
            {/* Credit Usage Distribution */}
            {creditUsageStats && (
              <Card.Section title="Credit Usage Distribution">
                <Stack vertical spacing="loose">
                  <Stack distribution="fillEvenly" alignment="center">
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.usageByType.template || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Template Credits
                      </Text>
                      <Text variant="bodySm" as="p" color="subdued">
                        {creditUsageStats.usagePercentByType.template || 0}%
                      </Text>
                    </Stack>
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.usageByType.image || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Image Credits
                      </Text>
                      <Text variant="bodySm" as="p" color="subdued">
                        {creditUsageStats.usagePercentByType.image || 0}%
                      </Text>
                    </Stack>
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.usageByType.sizechart || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Size Chart Credits
                      </Text>
                      <Text variant="bodySm" as="p" color="subdued">
                        {creditUsageStats.usagePercentByType.sizechart || 0}%
                      </Text>
                    </Stack>
                    <Stack vertical alignment="center">
                      <Text variant="headingLg" as="h3">
                        {creditUsageStats.totalUsage || 0}
                      </Text>
                      <Text variant="bodySm" as="p">
                        Total Credits Used
                      </Text>
                    </Stack>
                  </Stack>
                  
                  {/* Pie Chart for Credit Usage Distribution */}
                  <div style={{ width: '100%', height: 250 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Template', value: creditUsageStats.usageByType.template || 0, fill: CHART_COLORS.primary },
                            { name: 'Image', value: creditUsageStats.usageByType.image || 0, fill: CHART_COLORS.secondary },
                            { name: 'Size Chart', value: creditUsageStats.usageByType.sizechart || 0, fill: CHART_COLORS.tertiary }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name) => [value, `${name} Credits`]}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Stack>
              </Card.Section>
            )}
            
            {/* Product Push Statistics */}
            <Card.Section title="Product Push Statistics">
              <Stack distribution="fillEvenly" alignment="center">
                <Stack vertical alignment="center">
                  <Text variant="headingLg" as="h3">
                    {creditStats?.totalPushes || 0}
                  </Text>
                  <Text variant="bodySm" as="p">
                    Total Products Pushed
                  </Text>
                </Stack>
                <Stack vertical alignment="center">
                  <Text variant="headingLg" as="h3">
                    {creditStats?.thisMonthPushes || 0}
                  </Text>
                  <Text variant="bodySm" as="p">
                    Products Pushed This Month
                  </Text>
                </Stack>
                <Stack vertical alignment="center">
                  <Text variant="headingLg" as="h3">
                    {creditStats?.lastMonthPushes || 0}
                  </Text>
                  <Text variant="bodySm" as="p">
                    Products Pushed Last Month
                  </Text>
                </Stack>
              </Stack>
            </Card.Section>
            
            {/* Monthly Credit Usage Chart */}
            {creditUsageStats && creditUsageStats.monthlyTrend && creditUsageStats.monthlyTrend.length > 0 && (
              <Card.Section title="Monthly Credit Usage Trend">
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <ComposedChart
                      data={creditUsageStats.monthlyTrend}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="monthName" 
                      />
                      <YAxis yAxisId="left" orientation="left" stroke={CHART_COLORS.primary} />
                      <YAxis yAxisId="right" orientation="right" stroke={CHART_COLORS.secondary} />
                       <RechartsTooltip 
                         labelFormatter={(value, entry, index) => {
                           // Use the original month value (YYYY-MM) to get the full date
                           const item = creditUsageStats.monthlyTrend[index];
                           if (item && item.month) {
                             const date = new Date(item.month + '-01');
                             return date.toLocaleString('default', { month: 'long', year: 'numeric' });
                           }
                           return value; // Fallback to the value if we can't parse it
                         }}
                         formatter={(value, name) => {
                           if (name === 'Template Credits') return [value, name];
                           if (name === 'Image Credits') return [value, name];
                           if (name === 'Size Chart Credits') return [value, name];
                           return [value, name];
                         }}
                       />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="credits"
                        name="Total Credits"
                        fill={CHART_COLORS.primary}
                        stroke={CHART_COLORS.primary}
                        fillOpacity={0.2}
                        yAxisId="left"
                      />
                      <Line
                        type="monotone"
                        dataKey="template"
                        name="Template Credits"
                        stroke={CHART_COLORS.secondary}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                        yAxisId="right"
                      />
                      <Line
                        type="monotone"
                        dataKey="image"
                        name="Image Credits"
                        stroke={CHART_COLORS.tertiary}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                        yAxisId="right"
                      />
                      <Line
                        type="monotone"
                        dataKey="sizechart"
                        name="Size Chart Credits"
                        stroke={CHART_COLORS.quaternary}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                        yAxisId="right"
                      />
                      <Brush dataKey="month" height={30} stroke={CHART_COLORS.brushStroke} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </Card.Section>
            )}
            
            {/* Daily Credit Usage Chart */}
            {creditUsageStats && creditUsageStats.dailyTrend && creditUsageStats.dailyTrend.length > 0 && (
              <Card.Section title="Daily Credit Usage Activity">
                {/* Debug info */}
                {console.log('Daily trend data in Dashboard:', JSON.stringify(creditUsageStats.dailyTrend))}
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <ComposedChart
                      data={creditUsageStats.dailyTrend}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(value) => {
                          try {
                            // Convert YYYY-MM-DD to MM-DD format
                            if (!value) return '';
                            
                            // Check if value is a valid date string
                            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
                            
                            const parts = value.split('-');
                            return `${parts[1]}-${parts[2]}`;
                          } catch (error) {
                            console.error('Error formatting date in chart:', error, value);
                            return '';
                          }
                        }}
                      />
                      <YAxis yAxisId="left" orientation="left" stroke={CHART_COLORS.primary} />
                      <YAxis yAxisId="right" orientation="right" stroke={CHART_COLORS.secondary} />
                      <RechartsTooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        labelFormatter={(value) => {
                          try {
                            // Convert YYYY-MM-DD to readable format
                            if (!value) return 'Unknown date';
                            
                            // Check if value is a valid date string
                            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
                            
                            const parts = value.split('-');
                            const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                            
                            if (isNaN(date.getTime())) {
                              return value; // Return original value if date is invalid
                            }
                            
                            return date.toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                          } catch (error) {
                            console.error('Error formatting tooltip date:', error, value);
                            return value;
                          }
                        }}
                        formatter={(value, name) => {
                          if (name === 'Template Credits') return [value, name];
                          if (name === 'Image Credits') return [value, name];
                          if (name === 'Size Chart Credits') return [value, name];
                          return [value, name];
                        }}
                        wrapperStyle={{ zIndex: 100 }}
                        isAnimationActive={false}
                      />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="credits"
                        name="Total Credits"
                        fill={CHART_COLORS.primary}
                        barSize={20}
                        radius={[4, 4, 0, 0]}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="template"
                        name="Template Credits"
                        stroke={CHART_COLORS.secondary}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="image"
                        name="Image Credits"
                        stroke={CHART_COLORS.tertiary}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="sizechart"
                        name="Size Chart Credits"
                        stroke={CHART_COLORS.quaternary}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 8 }}
                      />
                      <Brush dataKey="date" height={30} stroke={CHART_COLORS.brushStroke} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </Card.Section>
            )}
            

          </Card>
        </Layout.Section>
        {/* Template Usage */}
        {allShopsMetrics && allShopsMetrics.templateUsage && (
          <Layout.Section>
            <Card title="Template Usage">
              <Card.Section title="Template Performance">
                <Stack distribution="fillEvenly" alignment="center">
                  <Stack vertical alignment="center">
                    <Text variant="headingLg" as="h3">
                      {templateUsage.product}
                    </Text>
                    <Text variant="bodySm" as="p">
                      Products Processed
                    </Text>
                  </Stack>
                  <Stack vertical alignment="center">
                    <Text variant="headingLg" as="h3">
                      {templateUsage.variant}
                    </Text>
                    <Text variant="bodySm" as="p">
                      Variants Processed
                    </Text>
                  </Stack>
                  <Stack vertical alignment="center">
                    <Text variant="headingLg" as="h3">
                      {templates.length}
                    </Text>
                    <Text variant="bodySm" as="p">
                      Total Templates
                    </Text>
                  </Stack>
                </Stack>
              </Card.Section>
              <Card.Section title="Template Usage">
                <Stack distribution="fillEvenly">
                  <Stack vertical alignment="center">
                    <Text variant="headingLg" as="h3">
                      {allShopsMetrics.templateUsage.successful || 0}
                    </Text>
                    <Text variant="bodySm" as="p">
                      Templates Used Successfully
                    </Text>
                  </Stack>
                  <Stack vertical alignment="center">
                    <Text variant="headingLg" as="h3">
                      {allShopsMetrics.templateUsage.failed || 0}
                    </Text>
                    <Text variant="bodySm" as="p">
                      Failed Template Uses
                    </Text>
                  </Stack>
                  <Stack vertical alignment="center">
                    <Text variant="headingLg" as="h3">
                      {allShopsMetrics?.templateUsage?.successful || 0}
                    </Text>
                    <Text variant="bodySm" as="p">
                      Templates Used Successfully
                    </Text>
                  </Stack>
                </Stack>
              </Card.Section>
            </Card>
          </Layout.Section>
        )}
            
        {/* Template Usage Across All Shops */}
        {allShopsMetrics && allShopsMetrics.templateUsage && allShopsMetrics.templateUsage.total > 0 && (
          <Layout.Section>
            <Card title="Template Usage Across All Shops">
              <Card.Section>
                <Stack vertical spacing="loose">
                  <Stack vertical spacing="tight">
                    <Text variant="bodyMd" as="p">
                      Total Templates Used: {allShopsMetrics?.templateUsage?.total || 0}
                    </Text>
                    <Text variant="bodyMd" as="p">
                      Success Rate: {allShopsMetrics?.templateUsage?.successRate || 0}%
                    </Text>
                    <Stack distribution="leading" spacing="tight">
                      <Stack.Item>
                        <Text variant="bodyMd" as="span">
                          Successful: {allShopsMetrics?.templateUsage?.successful || 0}
                        </Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Text variant="bodyMd" as="span">
                          Failed: {allShopsMetrics?.templateUsage?.failed || 0}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  </Stack>
                  
                  {/* Top templates table */}
                  {allShopsMetrics?.templateUsage?.byTemplate && allShopsMetrics.templateUsage.byTemplate.length > 0 && (
                    <div>
                      <Text variant="headingSm" as="h4" style={{ marginBottom: '10px' }}>
                        Top Templates
                      </Text>
                      <DataTable
                        columnContentTypes={['text', 'numeric', 'numeric']}
                        headings={['Template', 'Uses', 'Success Rate']}
                        rows={allShopsMetrics.templateUsage.byTemplate.slice(0, 5).map(template => {
                          const templateData = templates.find(t => t.id === template.templateId);
                          const successRate = template.count > 0 ? 
                            ((template.successful / template.count) * 100).toFixed(1) : '0.0';
                          
                          return [
                            templateData ? templateData.name : template.templateId,
                            template.count,
                            `${successRate}%`
                          ];
                        })}
                      />
                    </div>
                  )}
                </Stack>
              </Card.Section>
            </Card>
          </Layout.Section>
        )}

        {/* SECTION 3: SHOPIFY OPERATIONS */}
        <Layout.Section>
          <div style={{ marginBottom: '16px' }}>
            <TextContainer spacing="tight">
              <Stack alignment="center" spacing="tight">
                <Text variant="headingMd" as="h2">Shopify Operations</Text>
                <Tooltip content={sectionDescriptions.shopManagement}>
                  <Button plain icon={InfoMinor} accessibilityLabel="Learn more about Shopify Operations" />
                </Tooltip>
              </Stack>
              <Text variant="bodySm" as="p">Statistics about your product pushes and image processing operations.</Text>
            </TextContainer>
          </div>
          <Card>
            {/* Product Push Statistics */}
            <Card.Section title="Product Push Statistics">
              {creditStats ? (
                <Stack vertical spacing="loose">
                  <TextContainer>
                    <Stack distribution="equalSpacing">
                      <Text variant="headingMd">Monthly Usage</Text>
                      <Text>This Month: {creditStats.thisMonth} pushes</Text>
                    </Stack>
                    <Stack distribution="equalSpacing">
                      <Text>Last Month: {creditStats.lastMonth} pushes</Text>
                      <Text>Daily Average: {creditStats.dailyAverage} pushes/day</Text>
                    </Stack>
                  </TextContainer>
                  
                  <Text variant="headingMd" as="h3" alignment="center" style={{ marginBottom: '10px', textAlign: 'center' }}>
                    Monthly Product Push Activity
                  </Text>
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <ComposedChart
                        data={creditStats.monthlyPushTrend || []}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="month" 
                          tickFormatter={(value) => {
                            // Convert YYYY-MM to Month name
                            const date = new Date(value + '-01');
                            return date.toLocaleString('default', { month: 'short' });
                          }}
                        />
                        <YAxis yAxisId="left" orientation="left" stroke="#008060" />
                        <RechartsTooltip 
                          cursor={{ strokeDasharray: '3 3' }}
                          labelFormatter={(value) => {
                            // Convert YYYY-MM to Month Year format
                            const date = new Date(value + '-01');
                            return date.toLocaleString('default', { month: 'long', year: 'numeric' });
                          }}
                          formatter={(value) => [`${value} pushes`, 'Product Pushes']}
                          wrapperStyle={{ zIndex: 100 }}
                          isAnimationActive={false}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="pushes"
                          name="Product Pushes"
                          fill="#008060"
                          stroke="#008060"
                          fillOpacity={0.2}
                          yAxisId="left"
                          dot={{ r: 4 }}
                          activeDot={{ r: 8 }}
                        />
                        <Brush dataKey="month" height={30} stroke={CHART_COLORS.brushStroke} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {creditStats.dailyPushTrend && creditStats.dailyPushTrend.length > 0 && (
                    <div style={{ width: '100%', height: 300, marginTop: '20px' }}>
                      <Text variant="headingMd" as="h3" alignment="center" style={{ marginBottom: '10px', textAlign: 'center' }}>
                        Daily Product Push Activity
                      </Text>
                      <ResponsiveContainer>
                        <ComposedChart
                          data={creditStats.dailyPushTrend || []}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => {
                              // Convert YYYY-MM-DD to MM-DD format
                              const date = new Date(value);
                              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                            }}
                          />
                          <YAxis yAxisId="left" orientation="left" stroke="#008060" />
                          <RechartsTooltip 
                            cursor={{ strokeDasharray: '3 3' }}
                            labelFormatter={(value) => {
                              // Convert YYYY-MM-DD to readable format
                              const date = new Date(value);
                              return date.toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                            }}
                            formatter={(value) => [`${value} pushes`, 'Product Pushes']}
                            wrapperStyle={{ zIndex: 100 }}
                            isAnimationActive={false}
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="pushes"
                            name="Product Pushes"
                            fill="#008060"
                            barSize={20}
                            radius={[4, 4, 0, 0]}
                          />
                          <Brush dataKey="date" height={30} stroke={CHART_COLORS.brushStroke} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Stack>
              ) : (
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  <Spinner />
                </div>
              )}
            </Card.Section>
          </Card>
        </Layout.Section>
         {/* Image Processing Overview - Part of Shopify Operations */}
         <Layout.Section>
          <Card title="Image Processing Overview">
            <Card.Section>
              {imageMetrics ? (
                <Stack vertical spacing="loose">
                  <Stack distribution="fillEvenly" alignment="center">
                    <Stack vertical spacing="extraTight" alignment="center">
                      <Text variant="headingLg" as="h3">
                        {imageMetrics.totalStats.imagesProcessed}
                      </Text>
                      <Text variant="bodyMd" as="p">
                        Images Processed
                      </Text>
                    </Stack>

                    <Stack vertical spacing="extraTight" alignment="center">
                      <Text variant="headingLg" as="h3">
                        {Math.round(imageMetrics.totalStats.storageSaved / (1024 * 1024))} MB
                      </Text>
                      <Text variant="bodyMd" as="p">
                        Storage Saved
                      </Text>
                    </Stack>

                    <Stack vertical spacing="extraTight" alignment="center">
                      <Text variant="headingLg" as="h3">
                        {Number(imageMetrics.totalStats.compressionRatio || 0).toFixed(1)}%
                      </Text>
                      <Text variant="bodyMd" as="p">
                        Avg. Compression
                      </Text>
                    </Stack>
                  </Stack>

                  {/* Monthly data chart */}
                  {imageMetrics.monthlyData && imageMetrics.monthlyData.length > 0 && (
                      <div style={{ width: '100%', height: 300, marginTop: '20px' }}>
                      <Text variant="headingMd" as="h3" alignment="center" style={{ marginBottom: '10px', textAlign: 'center' }}>
                        Monthly Image Processing Activity
                      </Text>
                      <ResponsiveContainer>
                        <ComposedChart
                          data={imageMetrics.monthlyData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="month" 
                            tickFormatter={(value) => {
                              // Convert YYYY-MM to Month name
                              const date = new Date(value + '-01');
                              return date.toLocaleString('default', { month: 'short' });
                            }}
                          />
                          <YAxis yAxisId="left" orientation="left" stroke="#008060" />
                          <YAxis yAxisId="right" orientation="right" stroke="#47c1bf" />
                          <RechartsTooltip 
                            cursor={{ strokeDasharray: '3 3' }}
                            labelFormatter={(value) => {
                              // Convert YYYY-MM to Month Year format
                              const date = new Date(value + '-01');
                              return date.toLocaleString('default', { month: 'long', year: 'numeric' });
                            }}
                            formatter={(value, name) => {
                              if (name === 'Storage Saved (MB)') {
                                return [Math.round(value), name];
                              }
                              return [value, name];
                            }}
                            wrapperStyle={{ zIndex: 100 }}
                            isAnimationActive={false}
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="imagesProcessed"
                            name="Images Processed"
                            fill="#008060"
                            barSize={20}
                            radius={[4, 4, 0, 0]}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="storageSaved"
                            name="Storage Saved (MB)"
                            stroke="#47c1bf"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                          />
                          <Brush dataKey="month" height={30} stroke={CHART_COLORS.brushStroke} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Daily data chart */}
                  {imageMetrics.dailyData && imageMetrics.dailyData.length > 1 && (
                    <div style={{ width: '100%', height: 300, marginTop: '20px' }}>
                      <Text variant="headingMd" as="h3" alignment="center" style={{ marginBottom: '10px', textAlign: 'center' }}>
                        Daily Processing Activity
                      </Text>
                      <ResponsiveContainer>
                        <ComposedChart
                          data={imageMetrics.dailyData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => {
                              // Convert YYYY-MM-DD to MM-DD format
                              const date = new Date(value);
                              return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                            }}
                          />
                          <YAxis yAxisId="left" orientation="left" stroke="#008060" />
                          <YAxis yAxisId="right" orientation="right" stroke="#47c1bf" />
                          <RechartsTooltip 
                            cursor={{ strokeDasharray: '3 3' }}
                            labelFormatter={(value) => {
                              // Convert YYYY-MM-DD to readable format
                              const date = new Date(value);
                              return date.toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                            }}
                            formatter={(value, name) => {
                              if (name === 'Storage Saved (MB)') {
                                return [Math.round(value), name];
                              }
                              return [value, name];
                            }}
                            wrapperStyle={{ zIndex: 100 }}
                            isAnimationActive={false}
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="imagesProcessed"
                            name="Images Processed"
                            fill="#008060"
                            barSize={20}
                            radius={[4, 4, 0, 0]}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="storageSaved"
                            name="Storage Saved (MB)"
                            stroke="#47c1bf"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 8 }}
                          />
                          <Brush dataKey="date" height={30} stroke="#8884d8" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Stack>
              ) : (
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                  <Spinner />
                </div>
              )}
            </Card.Section>
          </Card>
        </Layout.Section>

        {/* SECTION 4: SHOP MANAGEMENT */}
        <Layout.Section>
          <div style={{ marginBottom: '16px' }}>
            <TextContainer spacing="tight">
              <Stack alignment="center" spacing="tight">
                <Text variant="headingMd" as="h2">Shop Management</Text>
                <Tooltip content={sectionDescriptions.shopManagement}>
                  <Button plain icon={InfoMinor} accessibilityLabel="Learn more about Shop Management" />
                </Tooltip>
              </Stack>
              <Text variant="bodySm" as="p">Overview of your connected Shopify shops and their metrics.</Text>
            </TextContainer>
          </div>
          <Card>
            {/* Shop Breakdown */}
            <Card.Section title="Shop Metrics">
              {allShopsMetrics?.shopBreakdown && allShopsMetrics.shopBreakdown.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['Shop', 'Images Processed', 'Size Charts', 'Products Pushed', 'Credits Used', 'Storage Saved']}
                  rows={allShopsMetrics.shopBreakdown.map(shop => [
                    <Stack spacing="tight" alignment="center">
                      <Text variant="bodyMd" as="span">
                        {shop.name}
                      </Text>
                      <Button
                        plain
                        size="slim"
                        onClick={() => navigate(`/app/shops/${shop.id}`)}
                      >
                        View
                      </Button>
                    </Stack>,
                    shop.imagesProcessed,
                    shop.sizechartsGenerated || 0,
                    creditStats?.shopBreakdown?.[shop.id] || 0,
                    shop.creditsUsed || 0,
                    `${Math.round(shop.bytesSavedMB)} MB`
                  ])}
                />
              ) : (
                <EmptyState
                  heading="No shops connected"
                  action={{
                    content: 'Connect a Shop',
                    onAction: () => navigate('/app/shops/connect')
                  }}
                >
                  <p>Connect your first Shopify shop to get started.</p>
                </EmptyState>
              )}
            </Card.Section>
            
            {/* Recent Shops */}
            <Card.Section title="Recent Shops">
              {recentShops.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Shop', 'Type', 'Status', 'Actions']}
                  rows={recentShops.map(shop => [
                    shop.name || shop.domain || 'Unknown Shop',
                    shop.type || 'Standard',
                    shop.active ? (
                      <Badge status="success">Active</Badge>
                    ) : (
                      <Badge>Inactive</Badge>
                    ),
                    <Stack spacing="tight">
                      <Button
                        size="slim"
                        onClick={() => navigate(`/app/shops/${shop.id}`)}
                      >
                        View
                      </Button>
                      <Button
                        size="slim"
                        onClick={() => navigate(`/app/products?shop=${shop.id}`)}
                      >
                        Products
                      </Button>
                    </Stack>
                  ])}
                />
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center' }}>
                  <Text variant="bodySm" color="subdued">No recent shops to display</Text>
                </div>
              )}
            </Card.Section>
            
            <Card.Section>
              <div style={{ textAlign: 'right' }}>
                <Button onClick={() => navigate('/app/shop-manager')}>Manage Shops</Button>
              </div>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default Dashboard;
